import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { ICreateTransaction, ITransaction } from '../interface/transaction';
import { IAPIError } from '../interface/common';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { post } from '../api/api';
import { showToast } from '../components/utils/toast';
import { useAnalytics } from './useAnalytics';
import { AnalyticsFeatureOperation } from '../interface/analytics';
type CreateTransactionWithParams = {
  data: ICreateTransaction;
  params?: Record<string, string>;
  activityId?: string;
  workTimeId?: string;
};
const createTransaction = async ({
  data,
  params,
}: CreateTransactionWithParams): Promise<ITransaction> => {
  return post(API_URLS.CREATE_TRANSACTION, data, { params, skipGlobalErrorHandler: true });
};
const createActivityTransaction = async ({
  data,
  params,
  activityId,
}: CreateTransactionWithParams): Promise<ITransaction> => {
  return post(API_URLS.CREATE_ACTIVITY_TRANSACTION.replace(':id', activityId ?? ''), data, {
    params,
    skipGlobalErrorHandler: true,
  });
};
const createWorkTimeTransaction = async ({
  data,
  params,
  workTimeId,
}: CreateTransactionWithParams): Promise<ITransaction> => {
  return post(`${API_URLS.WORK_TIMES}/${workTimeId}/create-transaction`, data, {
    params,
    skipGlobalErrorHandler: true,
  });
};

export const useCreateTransaction = (): UseMutationResult<
  ITransaction,
  AxiosError<IAPIError>,
  CreateTransactionWithParams
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();

  return useMutation({
    mutationFn: (params: CreateTransactionWithParams) => {
      if (params?.activityId) {
        return createActivityTransaction(params);
      }
      if (params?.workTimeId) {
        return createWorkTimeTransaction(params);
      }

      const { activityId, workTimeId, ...rest } = params;
      return createTransaction(rest);
    },
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.TRANSACTION_CREATION);
      showToast('success', t('transaction:successfullyCreated'));
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
